import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
    Card, CardContent, CardActions, Typography, Box, Grid, IconButton, TextField,
    Dialog, DialogActions, DialogContent, DialogTitle, Button, CircularProgress
} from '@mui/material';
import {
    CheckCircleOutline as CheckIcon,
    HighlightOff as RejectIcon,
    Edit as EditIcon,
    Save as SaveIcon,
    Close as CancelIcon,
    ArrowBackIos as PrevIcon,
    ArrowForwardIos as NextIcon,
} from '@mui/icons-material';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SuggestedSportsComponent = () => {
    const [suggestions, setSuggestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingId, setEditingId] = useState(null);
    const [editText, setEditText] = useState('');
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [sportToProcess, setSportToProcess] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(6);

    // --- Fetch suggestions ---
    const fetchSuggestions = useCallback(async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API_URL}/api/suggested-activities/grouped`);
            setSuggestions(response.data);
        } catch (error) {
            console.error("Error fetching suggestions:", error);
            alert("Failed to load suggestions. Check console.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchSuggestions();
    }, [fetchSuggestions]);

    // --- Edit Flow ---
    const handleEdit = (group) => {
        setEditingId(group._id);
        setEditText(group._id);
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setEditText('');
    };

    const handleSaveEdit = async (group) => {
        const sourceIds = group.items.map(s => s.id);
        try {
            await Promise.all(
                sourceIds.map(id =>
                    axios.patch(`${API_URL}/api/suggested-activities/${id}`, {
                        name: editText
                    })
                )
            );

            handleCancelEdit();
            await fetchSuggestions();
        } catch (error) {
            console.error("Error saving edit:", error);
            alert("Failed to save changes.");
        }
    };

    // --- Reject Flow ---
    const handleReject = async (group) => {
        const sourceIds = group.items.map(s => s.id);
        try {
            await axios.post(`${API_URL}/api/suggested-activities/reject`, { ids: sourceIds });
            await fetchSuggestions();
        } catch (error) {
            console.error("Error rejecting:", error);
            alert("Failed to reject suggestion.");
        }
    };

    // --- Create new official category ---
    const handleOpenConfirmModal = (group) => {
        setSportToProcess(group);
        setConfirmModalOpen(true);
    };

    const handleCloseConfirmModal = () => {
        setSportToProcess(null);
        setConfirmModalOpen(false);
    };

    const handleConfirmCreate = async () => {
        if (!sportToProcess) return;

        const sourceIds = sportToProcess.items.map(s => s.id);

        try {
            await axios.post(`${API_URL}/api/suggested-activities/create-sport`, {
                name: sportToProcess._id,
                sourceIds
            });

            handleCloseConfirmModal();
            await fetchSuggestions();
        } catch (error) {
            console.error("Error creating sport:", error);
            alert("Failed to create sport.");
        }
    };

    // Pagination
    const indexOfLast = currentPage * itemsPerPage;
    const indexOfFirst = indexOfLast - itemsPerPage;
    const currentSuggestions = suggestions.slice(indexOfFirst, indexOfLast);
    const totalPages = Math.ceil(suggestions.length / itemsPerPage);

    return (
        <>
            <Card sx={{ backgroundColor: "#202020", color: "#fff", borderRadius: "12px", height: '100%', p: 1 }}>
                <CardContent sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>

                    {/* Header */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Typography variant="h5">Trending Category Suggestions</Typography>

                        {!loading && totalPages > 1 && (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <IconButton onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} sx={{ color: '#fff' }}>
                                    <PrevIcon />
                                </IconButton>

                                <Typography sx={{ mx: 2 }}>
                                    Page {currentPage} of {totalPages}
                                </Typography>

                                <IconButton onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} sx={{ color: '#fff' }}>
                                    <NextIcon />
                                </IconButton>
                            </Box>
                        )}
                    </Box>

                    {/* Content Area */}
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', minHeight: '300px' }}>
                            <CircularProgress sx={{ color: '#12c099' }} />
                        </Box>
                    ) : suggestions.length === 0 ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', minHeight: '300px' }}>
                            <Typography>No new suggestions found.</Typography>
                        </Box>
                    ) : (
                        <Grid container spacing={2}>
                            {currentSuggestions.map(group => (
                                <Grid item key={group._id} xs={12} sm={6} md={4}>
                                    <Card sx={{ backgroundColor: '#2C2C2E', height: '100%', transition: '0.2s', '&:hover': { transform: 'scale(1.03)' }}}>
                                        <CardContent sx={{ textAlign: 'center' }}>
                                            {editingId === group._id ? (
                                                <TextField
                                                    variant="standard"
                                                    value={editText}
                                                    onChange={e => setEditText(e.target.value)}
                                                    autoFocus
                                                    fullWidth
                                                    sx={{
                                                        input: { color: '#fff', textAlign: 'center', fontSize: '1.25rem' }
                                                    }}
                                                />
                                            ) : (
                                                <>
                                                    <Typography variant="h2" sx={{ color: '#12c099' }}>
                                                        {group.totalCount}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ color: '#AEAEB2' }}>
                                                        Suggestions
                                                    </Typography>
                                                    <Typography variant="h6">{group._id}</Typography>
                                                </>
                                            )}
                                        </CardContent>

                                        <CardActions sx={{ justifyContent: 'space-around', backgroundColor: '#3A3A3C' }}>
                                            {editingId === group._id ? (
                                                <>
                                                    <IconButton sx={{ color: '#12c099' }} onClick={() => handleSaveEdit(group)}>
                                                        <SaveIcon />
                                                    </IconButton>
                                                    <IconButton sx={{ color: '#AEAEB2' }} onClick={handleCancelEdit}>
                                                        <CancelIcon />
                                                    </IconButton>
                                                </>
                                            ) : (
                                                <>
                                                    <IconButton sx={{ color: '#AEAEB2' }} onClick={() => handleEdit(group)}>
                                                        <EditIcon />
                                                    </IconButton>
                                                    <IconButton sx={{ color: '#ff453a' }} onClick={() => handleReject(group)}>
                                                        <RejectIcon />
                                                    </IconButton>
                                                    <IconButton sx={{ color: '#30d158' }} onClick={() => handleOpenConfirmModal(group)}>
                                                        <CheckIcon />
                                                    </IconButton>
                                                </>
                                            )}
                                        </CardActions>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    )}
                </CardContent>
            </Card>

            {/* Modal */}
            <Dialog open={confirmModalOpen} onClose={handleCloseConfirmModal}
                    PaperProps={{ sx: { backgroundColor: '#2C2C2E', color: '#fff' } }}>

                <DialogTitle>Create New Category?</DialogTitle>

                <DialogContent>
                    <Typography>
                        Create an official category for:{" "}
                        <strong style={{ color: '#12c099' }}>{sportToProcess?._id}</strong>
                    </Typography>
                </DialogContent>

                <DialogActions>
                    <Button onClick={handleCloseConfirmModal} sx={{ color: '#AEAEB2' }}>Cancel</Button>
                    <Button onClick={handleConfirmCreate}
                            sx={{ backgroundColor: '#12c099', color: '#fff' }}>
                        Confirm & Create
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default SuggestedSportsComponent;
